/* Auth + billing + team client for the product-led-growth flows.
 *
 * Talks to the API's /api/auth, /api/billing, /api/team endpoints when it's
 * reachable (real Google/Microsoft SSO, email magic-link, sessions). When the
 * backend isn't there — a standalone demo — it falls back to a localStorage
 * session so sign-in, plan changes, and invites still work end to end. */

const API = (import.meta.env.VITE_API_URL as string | undefined) || "/api";
const LS = "pen:session";
const LS_BILLING = "pen:billing";
const LS_TEAM = "pen:team";

export interface AuthUser { id: string; name: string; email: string; avatarUrl?: string; provider: string; }
export interface Plan { id: string; name: string; priceMonthly?: number | null; seatLimit: number; status: "trialing" | "active" | "free"; trialEndsAt?: string; }
export interface Me { user: AuthUser; workspace: { id: string; name: string }; membership: { role: string }; plan: Plan; }
// `providers.google/microsoft` stay true when AWS Cognito is the upstream IdP
// federating them, so the same buttons work whether SSO is direct or via Cognito.
export interface AuthConfig { providers: { google: boolean; microsoft: boolean; cognito?: boolean }; email: boolean; devAllowed: boolean; }
export interface PlanCatalogItem { id: string; name: string; priceMonthly: number | null; seatLimit: number; }
export interface Member { id?: string; userId?: string; name?: string; email: string; role: string; }
export interface Invite { id: string; email: string; role: string; status: string; link?: string; }

async function req<T>(path: string, opts?: RequestInit, timeoutMs = 6000): Promise<T | null> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(`${API}${path}`, { credentials: "include", headers: { "content-type": "application/json" }, signal: ctrl.signal, ...opts });
    clearTimeout(t);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

// ---------- local fallback session ----------
const TRIAL_END = "2026-08-30";
function localMe(user: Partial<AuthUser>): Me {
  return {
    user: { id: "local", name: user.name ?? "Demo User", email: user.email ?? "demo@penumbrai.ai", provider: user.provider ?? "demo", avatarUrl: user.avatarUrl },
    workspace: { id: "ws-demo", name: "Northstar SaaS" },
    membership: { role: "owner" },
    plan: readLocalBilling().plan,
  };
}
function readLocal(): Me | null { try { const r = localStorage.getItem(LS); return r ? JSON.parse(r) : null; } catch { return null; } }
function writeLocal(me: Me | null) { try { me ? localStorage.setItem(LS, JSON.stringify(me)) : localStorage.removeItem(LS); } catch { /* ignore */ } }

// ---------- config / session ----------
// Only a SUCCESSFUL config response is cached. A failed fetch returns the
// offline default but is not remembered, so a transient failure on one load
// doesn't poison the whole session into thinking Google is unavailable.
let cachedConfig: AuthConfig | null = null;
const OFFLINE_CONFIG: AuthConfig = { providers: { google: false, microsoft: false, cognito: false }, email: true, devAllowed: true };
export async function getConfig(): Promise<AuthConfig> {
  if (cachedConfig) return cachedConfig;
  const live = await req<AuthConfig>("/auth/config");
  if (live) cachedConfig = live;
  return live ?? OFFLINE_CONFIG;
}

export async function getMe(): Promise<Me | null> {
  const remote = await req<Me>("/auth/me");
  if (remote) { writeLocal(remote); return remote; }
  // 401 vs unreachable: if the API answered config but not me, treat as signed-out.
  return readLocal();
}

export async function signInDemo(user?: Partial<AuthUser>): Promise<Me> {
  const remote = await req<Me>("/auth/dev", { method: "POST", body: JSON.stringify({ email: user?.email, name: user?.name }) });
  if (remote) { writeLocal(remote); return remote; }
  const me = localMe(user ?? {});
  writeLocal(me);
  return me;
}

/** SSO: a full-page navigation to the API's start endpoint, which 302s to the
 * real identity provider (Google/Microsoft, direct or via Cognito) and then
 * back to the app with a session. This is deliberately NOT gated on the
 * background `/auth/config` check — when someone explicitly clicks "Continue
 * with Google" they must be taken to Google, even if a flaky config fetch would
 * otherwise make the app think the provider is unavailable and silently fall
 * back to the demo. (A standalone/offline demo with no backend should use the
 * "Continue with a demo account" button instead.) */
export async function signInWith(provider: "google" | "microsoft", redirect = "/"): Promise<Me | null> {
  window.location.href = `${API}/auth/${provider}/start?redirect=${encodeURIComponent(redirect)}`;
  return null; // navigating away to the real identity provider
}

export async function signInEmail(email: string): Promise<{ sent: boolean; devLink?: string }> {
  const remote = await req<{ sent: boolean; devLink?: string }>("/auth/email/start", { method: "POST", body: JSON.stringify({ email }) });
  if (remote) return remote;
  // offline: sign in immediately as that email
  await signInDemo({ email, name: email.split("@")[0], provider: "email" });
  return { sent: true };
}

export async function signOut(): Promise<void> {
  await req("/auth/logout", { method: "POST" });
  writeLocal(null);
}

// ---------- billing (self-serve) ----------
// A Free tier and one paid Team plan ($30 / CSM · mo). Everything in the
// platform is included; only risk agents and automations are à la carte, and
// AI analyses are a hard monthly cap. Mirrors api/src/auth/store.ts.
export const PLAN_CATALOG: PlanCatalogItem[] = [
  { id: "free", name: "Free", priceMonthly: 0, seatLimit: 2 },
  { id: "team", name: "Team", priceMonthly: 30, seatLimit: 999 },
];
function readLocalBilling(): { plan: Plan; seatsUsed: number } {
  try { const r = localStorage.getItem(LS_BILLING); if (r) return JSON.parse(r); } catch { /* ignore */ }
  return { plan: { id: "team", name: "Team", priceMonthly: 30, seatLimit: 999, status: "trialing", trialEndsAt: TRIAL_END }, seatsUsed: 1 };
}
function writeLocalBilling(b: { plan: Plan; seatsUsed: number }) { try { localStorage.setItem(LS_BILLING, JSON.stringify(b)); } catch { /* ignore */ } }

export async function getBilling(): Promise<{ plan: Plan; catalog: PlanCatalogItem[]; seatsUsed: number }> {
  const remote = await req<{ plan: Plan; catalog: PlanCatalogItem[]; seatsUsed: number }>("/billing");
  if (remote) return remote;
  const b = readLocalBilling();
  return { ...b, catalog: PLAN_CATALOG };
}
export async function changePlan(planId: string): Promise<Plan> {
  const remote = await req<{ plan: Plan }>("/billing/change", { method: "POST", body: JSON.stringify({ planId }) });
  if (remote) { const me = readLocal(); if (me) { me.plan = remote.plan; writeLocal(me); } return remote.plan; }
  const item = PLAN_CATALOG.find((p) => p.id === planId) ?? PLAN_CATALOG[0];
  const plan: Plan = { id: item.id, name: item.name, priceMonthly: item.priceMonthly, seatLimit: item.seatLimit, status: item.id === "free" ? "free" : "active" };
  const b = readLocalBilling(); b.plan = plan; writeLocalBilling(b);
  const me = readLocal(); if (me) { me.plan = plan; writeLocal(me); }
  return plan;
}

// ---------- team (self-serve invites) ----------
function readLocalTeam(): { members: Member[]; invites: Invite[] } {
  try { const r = localStorage.getItem(LS_TEAM); if (r) return JSON.parse(r); } catch { /* ignore */ }
  const me = readLocal();
  return { members: [{ email: me?.user.email ?? "demo@penumbrai.ai", name: me?.user.name ?? "Demo User", role: "owner" }], invites: [] };
}
function writeLocalTeam(t: { members: Member[]; invites: Invite[] }) { try { localStorage.setItem(LS_TEAM, JSON.stringify(t)); } catch { /* ignore */ } }

export async function getTeam(): Promise<{ members: Member[]; invites: Invite[] }> {
  const remote = await req<{ members: Member[]; invites: Invite[] }>("/team");
  return remote ?? readLocalTeam();
}
export async function inviteMember(email: string, role: string): Promise<Invite> {
  const remote = await req<{ invite: Invite }>("/team/invite", { method: "POST", body: JSON.stringify({ email, role }) });
  if (remote) return remote.invite;
  const invite: Invite = { id: `inv_${Date.now().toString(36)}`, email, role, status: "pending", link: `https://app.penumbrai.ai/join?token=demo` };
  const t = readLocalTeam(); t.invites.push(invite); writeLocalTeam(t);
  return invite;
}
export async function revokeInvite(id: string): Promise<void> {
  const remote = await req("/team/revoke", { method: "POST", body: JSON.stringify({ id }) });
  if (remote) return;
  const t = readLocalTeam(); t.invites = t.invites.filter((i) => i.id !== id); writeLocalTeam(t);
}
